import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { colors } from '../../styles/theme';
import { SHARED_CHROME_SURFACE, withAlpha } from '../../Map/utils/sharedBranding';

/**
 * Miniaturas esquemáticas del selector de apariencia: no son screenshots, solo
 * transmiten la diferencia de layout entre la vista de Terrio y una branded.
 */

const BG_ELEVATED = colors.backgroundDark;
const BORDER = '#383838';
const TEXT_PRIMARY = '#F9FAFB';

const WIREFRAME_SHELL_SX = {
  width: '100%',
  aspectRatio: '16 / 10',
  borderRadius: '8px',
  bgcolor: '#1a1a1a',
  border: '1px solid rgba(255,255,255,0.08)',
  overflow: 'hidden',
};

const WIREFRAME_MAP_SX = {
  flex: 1.4,
  borderRadius: '5px',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(155deg, #3a4a38 0%, #243024 28%, #1f1f1f 72%)',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 72% 18%, rgba(255,255,255,0.07) 0%, transparent 42%)',
    pointerEvents: 'none',
  },
};

/** Polígono de lote esquemático sobre el mapa del thumbnail. */
function WireframeLotShape({ accent, fillOpacity = 0.22 }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: '28% 22%',
        borderRadius: '2px',
        border: `1.5px solid ${accent}`,
        bgcolor: withAlpha(accent, fillOpacity),
        boxShadow: `0 0 0 1px ${withAlpha(accent, 0.25)}`,
      }}
    />
  );
}

/** Mini fila de tabs del panel lateral. */
function WireframePanelTabs({ accent, activeIndex = 0 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.45, px: 0.45, pt: 0.35, pb: 0.15 }}>
      {[0, 1].map((index) => (
        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.15 }}>
          <Box
            sx={{
              height: 3,
              width: index === 0 ? 14 : 18,
              borderRadius: '1px',
              bgcolor: index === activeIndex ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.22)',
            }}
          />
          {index === activeIndex ? (
            <Box sx={{ height: 1.5, width: '100%', borderRadius: '99px', bgcolor: accent }} />
          ) : (
            <Box sx={{ height: 1.5, width: '100%' }} />
          )}
        </Box>
      ))}
    </Box>
  );
}

/** Tarjeta marketplace miniatura dentro del panel branded. */
function WireframeListingCard({ accent }) {
  return (
    <Box
      sx={{
        borderRadius: '3px',
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: 'rgba(255,255,255,0.04)',
        p: 0.45,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.3,
      }}
    >
      <Box sx={{ height: 3, width: '68%', borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.22)' }} />
      <Box sx={{ height: 2.5, width: '88%', borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.10)' }} />
      <Box sx={{ height: 2, width: '26%', borderRadius: '1px', bgcolor: accent, opacity: 0.9 }} />
      <Box sx={{ height: 4.5, width: '52%', borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.88)' }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.25, mt: 0.1 }}>
        {[0, 1, 2].map((key) => (
          <Box key={key}>
            <Box sx={{ height: 2, borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.14)' }} />
            <Box sx={{ height: 2.5, mt: 0.15, borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.32)' }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Footer de contacto miniatura (branded). */
function WireframeBrandedFooter({ accent }) {
  return (
    <Box
      sx={{
        height: 9,
        boxSizing: 'border-box',
        bgcolor: SHARED_CHROME_SURFACE,
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 0.55,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
        {[0, 1].map((key) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: accent, opacity: 0.95 }} />
            <Box sx={{ height: 2, width: key === 0 ? 10 : 8, borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.28)' }} />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Box sx={{ height: 2, width: 12, borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.18)' }} />
        <Box sx={{ height: 3.5, width: 8, borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.14)' }} />
      </Box>
    </Box>
  );
}

/** Acorta títulos largos en thumbnails (ej. "MI INMOBILIARIA ESCXSDAS..."). */
export function truncateCardTitle(text, maxLength = 20) {
  const normalized = String(text || '').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}...`;
}

const SKELETON_BAR_SX = {
  bgcolor: 'rgba(255,255,255,0.08)',
  borderRadius: '4px',
};

export const APPEARANCE_PICKER_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 1.25,
  minWidth: 0,
  width: '100%',
};

/** Misma columna que el desplegable: el marco alinea con el select. */
export const APPEARANCE_PREVIEW_COLUMN_SX = {
  width: '100%',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
};

/** Esquema al tamaño de una card de la grilla 2 columnas (`1fr 1fr`, gap 8px). */
const APPEARANCE_THUMB_SX = {
  width: 'calc((100% - 8px) / 2)',
  maxWidth: '100%',
  minWidth: 0,
};

/**
 * Misma geometría que el selector de apariencia (desplegable + thumbnail 16/10).
 * Placeholder de layout: sin copy en pantalla ni en aria-label.
 */
export function TemplatePreviewCardSkeleton() {
  return (
    <Box aria-hidden="true" sx={APPEARANCE_PICKER_SX}>
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{
          width: '100%',
          height: 42,
          borderRadius: '10px',
          bgcolor: 'rgba(255,255,255,0.08)',
        }}
      />
      <Box sx={APPEARANCE_PREVIEW_COLUMN_SX}>
        <Box
          sx={{
            width: '100%',
            p: 1.25,
            borderRadius: '10px',
            border: `1px solid ${BORDER}`,
            bgcolor: BG_ELEVATED,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{
              ...APPEARANCE_THUMB_SX,
              aspectRatio: '16 / 10',
              borderRadius: '8px',
              bgcolor: 'rgba(255,255,255,0.08)',
            }}
          />
        </Box>
        <Skeleton variant="rounded" animation="wave" width="88%" height={12} sx={SKELETON_BAR_SX} />
        <Skeleton variant="rounded" animation="wave" width="62%" height={12} sx={SKELETON_BAR_SX} />
      </Box>
    </Box>
  );
}

/** Marco al ancho del desplegable; el esquema queda chico, como una card de la grilla. */
export function TemplatePreview({ preview }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: '100%',
        p: 1.25,
        minWidth: 0,
        borderRadius: '10px',
        border: `1px solid ${BORDER}`,
        bgcolor: BG_ELEVATED,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={APPEARANCE_THUMB_SX}>
        {preview}
      </Box>
    </Box>
  );
}

/** Layout Terrio: mapa + panel, sin header ni footer de marca. */
export function TerrioWireframe() {
  const accent = colors.primary;
  const accentLight = colors.primaryLight;

  return (
    <Box sx={{ ...WIREFRAME_SHELL_SX, p: 0.75, display: 'flex', gap: 0.75 }}>
      <Box sx={WIREFRAME_MAP_SX}>
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            width: 18,
            height: 8,
            borderRadius: '2px',
            bgcolor: accent,
            opacity: 0.9,
            boxShadow: `0 0 6px ${withAlpha(accent, 0.35)}`,
          }}
        />
        <WireframeLotShape accent={accentLight} fillOpacity={0.18} />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 0 }}>
        <WireframePanelTabs accent={accent} activeIndex={0} />
        <Box sx={{ flex: 1, borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.05)', p: 0.45, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
          <Box sx={{ height: 6, borderRadius: '2px', bgcolor: 'rgba(255,255,255,0.16)', width: '72%' }} />
          <Box sx={{ height: 5, borderRadius: '2px', bgcolor: 'rgba(255,255,255,0.08)', width: '92%' }} />
          <Box sx={{ flex: 1, borderRadius: '3px', bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
        </Box>
        <Box sx={{ height: 10, borderRadius: '3px', bgcolor: accent, opacity: 0.75 }} />
      </Box>
    </Box>
  );
}

/** Layout branded: header de marca + mapa + panel + footer de contacto. */
export function BrandWireframe({ primary, logoUrl, brandInitial, brandName = '' }) {
  const brandLabel = truncateCardTitle(brandName || 'Mi marca', 14);

  return (
    <Box sx={{ ...WIREFRAME_SHELL_SX, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: 18,
          boxSizing: 'border-box',
          bgcolor: SHARED_CHROME_SURFACE,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          px: 0.75,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45, minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '2px',
              bgcolor: logoUrl ? 'rgba(255,255,255,0.06)' : withAlpha(primary, 0.22),
              border: `1px solid ${withAlpha(primary, 0.65)}`,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: logoUrl ? 'none' : `0 0 4px ${withAlpha(primary, 0.25)}`,
            }}
          >
            {logoUrl ? (
              <Box component="img" src={logoUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Typography sx={{ fontSize: 6, fontWeight: 800, color: primary, lineHeight: 1 }}>
                {brandInitial}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 6.5,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.88)',
              lineHeight: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {brandLabel}
          </Typography>
        </Box>
        <Box
          sx={{
            height: 6,
            px: 0.35,
            borderRadius: '1px',
            border: `1px solid ${BORDER}`,
            bgcolor: SHARED_CHROME_SURFACE,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box sx={{ height: 2, width: 14, borderRadius: '1px', bgcolor: 'rgba(232,244,244,0.55)' }} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 0.5, p: 0.5, minHeight: 0 }}>
          <Box sx={{ ...WIREFRAME_MAP_SX, flex: 1.35 }}>
            <WireframeLotShape accent={primary} fillOpacity={0.14} />
            <Box
              sx={{
                position: 'absolute',
                left: 4,
                bottom: 4,
                width: 10,
                height: 14,
                borderRadius: '2px',
                bgcolor: 'rgba(20,20,20,0.72)',
                border: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                p: 0.25,
              }}
            >
              {[0, 1, 2].map((key) => (
                <Box key={key} sx={{ height: 1.5, borderRadius: '1px', bgcolor: 'rgba(255,255,255,0.12)' }} />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              borderRadius: '4px',
              bgcolor: SHARED_CHROME_SURFACE,
              border: '1px solid rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ height: 2, bgcolor: primary, flexShrink: 0 }} />
            <WireframePanelTabs accent={primary} activeIndex={0} />
            <Box sx={{ flex: 1, px: 0.45, pb: 0.35, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
              <WireframeListingCard accent={primary} />
              <Box sx={{ flex: 1, borderRadius: '2px', bgcolor: 'rgba(255,255,255,0.03)' }} />
            </Box>
            <WireframeBrandedFooter accent={primary} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Chip del desplegable de apariencia (Principal / Por defecto). */
function AppearanceChip({ children }) {
  return (
    <Box
      component="span"
      sx={{
        px: 0.75,
        py: 0.15,
        borderRadius: '99px',
        bgcolor: 'rgba(255,255,255,0.10)',
        color: TEXT_PRIMARY,
        fontSize: '10px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

/** Chip "Principal" junto a la identidad marcada como default. */
export function PrincipalBadge() {
  return <AppearanceChip>Principal</AppearanceChip>;
}

/** Chip de Terrio: la vista de la plataforma, siempre disponible. */
export function TerrioDefaultBadge() {
  return <AppearanceChip>Por defecto</AppearanceChip>;
}
